import { useEffect, useMemo, useState } from 'react';
import { filterItems } from '@forecastify/shared';
import { inTeam, settingsTeamFilter } from '../use-teams';
import type {
  LeaveAttachmentRule,
  LeaveCarryOver,
  LeaveNoteRule,
  LeaveQuota,
  LeaveQuotaSource,
  LeaveType,
} from '@forecastify/shared';
import {
  Icon,
  ListPager,
  Select,
  Switch,
  Tabs,
  TextInput,
  type IconName,
  Checkbox,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import { EditorDialog } from './editor-dialog';
import { usePages } from '../use-pages';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';
import { Field } from '../card';
import { LEAVE_SYMBOLS, leaveIcon } from '../leave/people-look';
import { identityColor, initialsOf } from '../identity';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Szabadságkezelés. C46, two tabs.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * **Szabadság típusok** — the vocabulary, with its rules. **Szabadság kvóták** — a year × person ×
 * type grid of whole days (P3-2).
 *
 * ⚠️⚠️ **REBUILT AGAINST THE MOCKUP, 11 Sep 2026.** What changed, and why each of them is a decision:
 *
 * 1. **A type is a BLOCK, not a table row.** Its six rules — quota source, carry-over and its
 *    deadline, the note policy, the attachment policy, back-dating — do not fit in six columns, and
 *    ours showed two of them. The mockup writes them as two sentences under the name, which is the
 *    only shape that fits all six and reads as what the type DOES.
 * 2. **The icon is on the row and choosable.** `leave_types.icon` has always crossed the wire and
 *    the form never sent it, so every type kept the seeded glyph forever — including the one that
 *    lost its icon entirely.
 * 3. **The order is FIXED** — 15 Sep. It was draggable because the mockup's footnote said the
 *    request form follows it; that footnote went in the same pass, and so did the reason.
 *    An order nobody can change is not an order, it is an accident of insertion.
 * 4. **The quota grid gained a search, a year picker in its own header, a per-person total and a
 *    footer count** — a grid of forty people and four types is otherwise a wall.
 *
 * ⚠️⚠️ **THE FOOTNOTE NAMING THE EXCLUDED PEOPLE IS PART OF THE GRID, NOT DECORATION.** Leave
 * management is per person; somebody handled by a payroll system elsewhere has no cells at all, and
 * without the note their absence reads as a bug in the grid rather than as the decision it is.
 *
 * ⚠️ **A blank cell and a `0` are different answers** — no entitlement of that type versus an
 * entitlement of nothing — so clearing a cell removes the row and typing `0` writes one.
 *
 * ⚠️ **The mockup's per-cell *"n felhasználva · m függő"* is NOT drawn, and that is deliberate.**
 * Neither figure is on `leaveQuotaSchema`; inventing them here would mean either a second request
 * per cell or a schema addition nobody has asked for (rule 8). What the cell does carry is P3-8's
 * carried days, which the mockup has no equivalent of.
 */

export interface LeaveTypeDraft {
  readonly id: string | null;
  readonly name: string;
  readonly icon: string;
  readonly active: boolean;
  readonly backdate: boolean;
  readonly quotaSource: LeaveQuotaSource;
  readonly carryOver: LeaveCarryOver;
  readonly carryToMonth: number | null;
  readonly noteRule: LeaveNoteRule;
  readonly attachmentRule: LeaveAttachmentRule;
  readonly version: number;
}

/** ⚠️ One builder, so the row's name and its kebab cannot open two different drafts. */
function draftOf(type: LeaveType): LeaveTypeDraft {
  return {
    id: type.id,
    name: type.name,
    icon: type.icon,
    active: type.active,
    backdate: type.backdate,
    quotaSource: type.quotaSource,
    carryOver: type.carryOver,
    carryToMonth: type.carryToMonth,
    noteRule: type.noteRule,
    attachmentRule: type.attachmentRule,
    version: type.version,
  };
}

export function LeaveSection(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly types: readonly LeaveType[];
  readonly quotas: readonly LeaveQuota[];
  readonly people: readonly {
    readonly id: string;
    readonly name: string;
    /** ⚠️ C68 · the generated face's seed. `null` where the person has no address. */
    readonly avatarSeed: string | null;
  }[];
  readonly excluded: readonly string[];
  /** R-60 · the team filter, on the client: the workspace's teams and who is in which. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly memberTeams?: ReadonlyMap<string, readonly string[]>;
  readonly year: number;
  readonly years: readonly number[];
  readonly canManage: boolean;
  readonly serverError: string | null;
  readonly onYear: (year: number) => void;
  readonly onSaveType: (draft: LeaveTypeDraft) => Promise<boolean>;
  readonly onDeleteType: (type: LeaveType) => void;
  readonly onSaveQuota: (memberId: string, leaveTypeId: string, days: number | null) => void;
  /** 17 Oct 2026 · *Másolás tavalyról* for the ticked people. Absent while nothing may be managed. */
  readonly onCopyQuotas?: (memberIds: readonly string[]) => void;
  /** The whole set, in its new order. Absent while nothing may be managed. */
  readonly onReorderTypes?: (ids: readonly string[]) => void;
  /** Until the quotas' own first answer — they are a second read. Skeleton rows, no count. */
  readonly quotasLoading?: boolean;
}) {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused — the dialog stays
     open until it lands, and shows its refusal inside. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [tab, setTab] = useState<'types' | 'quotas'>('types');
  const [draft, setDraft] = useState<LeaveTypeDraft | null>(null);
  const [query, setQuery] = useState('');
  const [teamId, setTeamId] = useState('');
  const [typeQuery, setTypeQuery] = useState('');
  const [typeStatus, setTypeStatus] = useState('');
  const [quotaType, setQuotaType] = useState('');
  /* 17 Oct 2026 · the people ticked for a copy — by person, so ticking one row ticks all of theirs. */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());

  /* ⚠️ Only the types that DRAW ON A QUOTA get a column: an unlimited type has no frame and a
     `none` type needs no entitlement, so a cell for either would be a box nobody can fill in. */
  const quotaTypes = props.types.filter((type) => type.quotaSource === 'quota');
  /* ⚠️ Twenty at a time — item 20's control, the one every other list in the product wears. */
  const pages = usePages(20);
  const { settle, reset, offset, limit } = pages;

  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return props.people.filter(
      (person) => needle === '' || person.name.toLowerCase().includes(needle),
    );
  }, [props.people, query]);

  /* ⚠️ The total is the FILTERED set, not the workspace — `page.ts`' rule, and what makes the
     selector's *"3 / 4"* true while a search is on. */

  /* ⚠️ The TYPES tab's own search and status filter — separate from the quota tab's, because
     they are searching different things and one box would clear under the reader on a tab change. */
  const shownTypes = useMemo(() => {
    const needle = typeQuery.trim().toLowerCase();
    return props.types.filter(
      (one) =>
        (needle === '' || one.name.toLowerCase().includes(needle)) &&
        (typeStatus === '' || (typeStatus === 'active') === one.active),
    );
  }, [props.types, typeQuery, typeStatus]);

  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ 17 Oct 2026 · EVERY MANAGED PERSON × EVERY QUOTA TYPE, EVERY YEAR. │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * The product owner: everybody whose leave is managed here appears for every year, at 0 where
   * nothing has been set, and the frame is typed in place — no add, no remove. The grid listed only
   * the rows that existed, so a new colleague or a new year was an empty page with no way to fill
   * it. A row with no stored frame is drawn at 0 and becomes a stored one the moment it is typed.
   *
   * ⚠️ **Still paged over the rows, not the people** — forty people and three types is a hundred
   * and twenty rows, and paging by person would hand out pages of wildly different sizes.
   */
  const entitlements = useMemo(() => {
    const stored = new Map(props.quotas.map((one) => [`${one.memberId}:${one.leaveTypeId}`, one]));
    const needle = query.trim().toLowerCase();
    return [...props.people]
      .filter(
        (person) =>
          (needle === '' || person.name.toLowerCase().includes(needle)) &&
          inTeam(props.memberTeams ?? new Map(), person.id, teamId),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .flatMap((person) =>
        quotaTypes
          .filter((type) => quotaType === '' || filterItems(quotaType).includes(type.id))
          .map((type) => ({
            person,
            type,
            quota: stored.get(`${person.id}:${type.id}`),
          })),
      );
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- the arrays are what this reads;
       `props` as a whole is a fresh object every render and would defeat the memo entirely. */
  }, [props.quotas, props.people, props.types, props.memberTeams, query, quotaType, teamId]);
  const shownPeople = useMemo(
    () => [...new Set(entitlements.map((entry) => entry.person.id))],
    [entitlements],
  );
  const allPicked = shownPeople.length > 0 && shownPeople.every((id) => picked.has(id));

  useEffect(() => {
    settle(tab === 'quotas' ? entitlements.length : matching.length);
  }, [settle, tab, entitlements.length, matching.length]);

  /**
   * ⚠️⚠️ **ONLY `KERET` IS EDITABLE, AND THE OTHER THREE ARE DELIBERATELY NOT.** *Felhasznált* is
   * the sum of approved requests, *maradék* is arithmetic over it, and *év* is which set of rows
   * you are looking at. Typing over any of them would make a figure mean two different things
   * depending on who touched it last.
   */
  const quotaSettingsRows: SettingsRow[] = useMemo(() => {
    return entitlements.slice(offset, offset + limit).map(({ quota, person, type }) => {
      /* ⚠️ What is left counts the CARRIED days in and the pending ones out — an approved
         fortnight is gone and a pending one is neither gone nor free. */
      const left =
        quota === undefined
          ? null
          : quota.days + quota.carriedDays - quota.usedDays - quota.pendingDays;
      return {
        id: `${person.id}:${type.id}`,
        cells: [
          {
            kind: 'check',
            on: picked.has(person.id),
            ariaLabel: t('settings.leave.pick', { name: person.name }),
            onChange: (next) => {
              setPicked((current) => {
                const out = new Set(current);
                if (next) out.add(person.id);
                else out.delete(person.id);
                return out;
              });
            },
          },
          {
            kind: 'name',
            text: person.name,
            avatar: {
              initials: initialsOf(person.name),
              color: identityColor(person.id),
              ...(person.avatarSeed === null ? {} : { seed: person.avatarSeed }),
            },
          },
          { kind: 'text', text: type.name },
          {
            kind: 'input',
            value: String(quota?.days ?? 0),
            ariaLabel: t('settings.leave.cell', { name: person.name, type: type.name }),
            disabled: !props.canManage,
            onCommit: (next) => {
              /* ⚠️ 17 Oct 2026 · blank is 0 now — there is no removing a row the grid always shows. */
              const raw = next.trim();
              props.onSaveQuota(
                person.id,
                type.id,
                raw === '' ? 0 : Math.max(0, Number(raw.replace(',', '.')) || 0),
              );
            },
          },
          { kind: 'num', text: quota === undefined ? '–' : quota.usedDays },
          {
            kind: 'num',
            text: left ?? '–',
            className: left !== null && left <= 2 ? 'text-destructive' : '',
          },
          { kind: 'num', text: props.year, className: 'text-muted-foreground' },
          { kind: 'blank' },
        ],
      };
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `props` is a fresh object each
       render; the fields this reads are listed. */
  }, [entitlements, offset, limit, props.year, props.canManage, picked, t]);

  const typeSettingsRows: SettingsRow[] = shownTypes.map((type) => ({
    id: type.id,
    onOpen: !props.canManage
      ? undefined
      : () => {
          setDraft(draftOf(type));
        },
    cells: [
      { kind: 'blank' },
      { kind: 'name', text: type.name, icon: leaveIcon(type.icon) },
      { kind: 'num', text: type.quotaCount },
      {
        kind: 'badge',
        text: type.active
          ? t('settings.schedules.status.active')
          : t('settings.schedules.status.inactive'),
        tone: type.active ? 'secondary' : 'outline',
        color: type.active ? 'var(--status-ok)' : 'var(--text-3)',
      },
      !props.canManage
        ? { kind: 'blank' }
        : {
            kind: 'kebab',
            ariaLabel: t('settings.leave.editType', { name: type.name }),
            items: [
              {
                label: t('common.edit'),
                icon: 'pencil',
                onSelect: () => {
                  setDraft(draftOf(type));
                },
              },
              {
                label: type.active
                  ? t('settings.schedules.deactivate')
                  : t('settings.schedules.activate'),
                icon: type.active ? 'moon' : 'sun',
                onSelect: () => {
                  void props.onSaveType({ ...draftOf(type), active: !type.active });
                },
              },
              { type: 'separator' },
              {
                label: t('common.delete'),
                icon: 'trash',
                destructive: true,
                /* ⚠️ Deleting a type takes its quota rows with it, so the count is the warning —
                   and a type somebody is entitled to is deactivated instead. */
                disabled: type.quotaCount > 0,
                onSelect: () => {
                  props.onDeleteType(type);
                },
              },
            ],
          },
    ],
  }));

  const newDraft = (): LeaveTypeDraft => ({
    id: null,
    name: '',
    icon: 'sun',
    active: true,
    backdate: true,
    quotaSource: 'quota',
    carryOver: 'none',
    carryToMonth: null,
    noteRule: 'optional',
    attachmentRule: 'optional',
    version: 0,
  });

  const onTypes = tab === 'types';

  return (
    <div data-screen="settings-leave" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.leave')}
        action={
          !props.canManage
            ? undefined
            : onTypes
              ? {
                  label: t('settings.leave.addType'),
                  icon: 'plus',
                  onSelect: () => {
                    setDraft(newDraft());
                  },
                }
              : props.onCopyQuotas === undefined
                ? undefined
                : {
                    /* 17 Oct 2026 · last year's frames for the ticked people. */
                    label: t('settings.leave.copy', { count: picked.size }),
                    icon: 'copy',
                    disabled: picked.size === 0,
                    onSelect: () => {
                      props.onCopyQuotas?.([...picked]);
                      setPicked(new Set());
                    },
                  }
        }
        tabs={
          <Tabs
            variant="underline"
            className="border-0"
            label={t('settings.nav.leave')}
            value={tab}
            onValueChange={(value) => {
              setTab(value === 'quotas' ? 'quotas' : 'types');
            }}
            items={[
              {
                value: 'types',
                label: t('settings.leave.tab.types'),
                icon: 'sun',
                ...(props.loading === true ? {} : { count: props.types.length }),
              },
              {
                value: 'quotas',
                label: t('settings.leave.tab.quotas'),
                icon: 'layout-grid',
                ...(props.quotasLoading === true ? {} : { count: props.people.length }),
              },
            ]}
          />
        }
        error={props.serverError}
        search={onTypes ? typeQuery : query}
        onSearch={
          onTypes
            ? setTypeQuery
            : (value) => {
                setQuery(value);
                /* ⚠️ A filter change resets the page — offset 40 in a set a search has cut to
                   three rows renders nothing, with no error to explain it. */
                reset();
              }
        }
        searchPlaceholder={
          onTypes ? t('settings.leave.searchTypes') : t('settings.leave.searchPeople')
        }
        filters={
          onTypes
            ? [
                {
                  key: 'status',
                  value: typeStatus,
                  ariaLabel: t('settings.schedules.column.status'),
                  options: [
                    { value: '', label: t('settings.schedules.status.all') },
                    { value: 'active', label: t('settings.schedules.status.active') },
                    { value: 'inactive', label: t('settings.schedules.status.inactive') },
                  ],
                  onChange: setTypeStatus,
                },
              ]
            : [
                {
                  key: 'year',
                  value: String(props.year),
                  ariaLabel: t('settings.holidays.year'),
                  options: props.years.map((year) => ({
                    value: String(year),
                    label: String(year),
                  })),
                  onChange: (value) => {
                    if (value === '') return;
                    props.onYear(Number(value));
                    reset();
                  },
                },
                {
                  key: 'type',
                  value: quotaType,
                  ariaLabel: t('settings.leave.column.type'),
                  /* 3 Nov 2026 · several types at once. */
                  multiPlaceholder: t('settings.leave.allTypes'),
                  options: quotaTypes.map((one) => ({ value: one.id, label: one.name })),
                  onChange: (value) => {
                    setQuotaType(value);
                    reset();
                  },
                },
                ...settingsTeamFilter(t, props.teams ?? [], teamId, (value) => {
                  setTeamId(value);
                  reset();
                }),
              ]
        }
      >
        {onTypes ? (
          <SettingsTable
            grid="26px minmax(0,1fr) 92px 116px 36px"
            minWidth={740}
            columns={[
              {},
              { label: t('settings.leave.column.name') },
              { label: t('settings.leave.column.quotas'), align: 'right' },
              { label: t('settings.schedules.column.status') },
              {},
            ]}
            groups={[{ rows: typeSettingsRows }]}
            loading={props.loading}
            empty={{
              icon: 'sun',
              title: t('settings.leave.emptyTypes'),
              body: t('settings.leave.emptyTypesBody'),
            }}
          />
        ) : (
          <>
            <SettingsTable
              grid="32px minmax(0,1fr) 150px 86px 106px 82px 62px 36px"
              minWidth={872}
              columns={[
                /* 17 Oct 2026 · ticks every person the search and type filter left. */
                {
                  header: (
                    <Checkbox
                      checked={allPicked}
                      label={allPicked ? t('settings.leave.pickNone') : t('settings.leave.pickAll')}
                      onCheckedChange={(next) => {
                        setPicked(next ? new Set(shownPeople) : new Set());
                      }}
                    />
                  ),
                },
                { label: t('settings.leave.column.person') },
                { label: t('settings.leave.column.type') },
                { label: t('settings.leave.column.frame'), align: 'right' },
                { label: t('settings.leave.column.used'), align: 'right' },
                { label: t('settings.leave.column.left'), align: 'right' },
                { label: t('settings.holidays.year'), align: 'right' },
                {},
              ]}
              groups={[{ rows: quotaSettingsRows }]}
              loading={props.quotasLoading}
              empty={{
                icon: 'sun',
                title: t('settings.leave.emptyQuotas'),
                body: t('settings.leave.emptyQuotasBody'),
              }}
            />
            <div className="mt-3 flex items-center justify-end">
              <ListPager
                page={{
                  hasPrevious: pages.hasPrevious,
                  hasNext: pages.hasNext,
                  onPrevious: pages.previous,
                  onNext: pages.next,
                  index: pages.index,
                  count: pages.count,
                  total: pages.total,
                  onGoTo: pages.goTo,
                  pageSize: pages.limit,
                }}
                labels={{
                  previousPage: t('list.previousPage'),
                  nextPage: t('list.nextPage'),
                  pageSelector: t('list.pageSelector'),
                  pageOf: (page, count) => t('list.pageOf', { page, count }),
                }}
              />
            </div>
          </>
        )}
      </SettingsPage>
      <EditorDialog
        open={draft !== null}
        title={
          draft?.id === null ? t('settings.leave.dialog.new') : t('settings.leave.dialog.edit')
        }
        saveDisabled={draft === null || draft.name.trim() === ''}
        onClose={() => {
          setSaveFailed(false);
          setDraft(null);
        }}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onSave={() => {
          if (draft === null) return;
          setSaving(true);
          void props.onSaveType(draft).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setDraft(null);
          });
        }}
      >
        {draft === null ? null : (
          <>
            <Field label={t('settings.leave.column.name')} required>
              <TextInput
                value={draft.name}
                placeholder={t('settings.leave.namePlaceholder')}
                onChange={(event) => {
                  setDraft({ ...draft, name: event.target.value });
                }}
              />
            </Field>

            <SymbolPicker
              value={draft.icon}
              onChange={(icon) => {
                setDraft({ ...draft, icon });
              }}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('settings.schedules.status')}>
                <Select
                  aria-label={t('settings.schedules.status')}
                  value={draft.active ? 'active' : 'inactive'}
                  onChange={(event) => {
                    setDraft({ ...draft, active: event.target.value === 'active' });
                  }}
                  options={[
                    { value: 'active', label: t('settings.schedules.status.active') },
                    { value: 'inactive', label: t('settings.schedules.status.inactive') },
                  ]}
                />
              </Field>
              {/**
               * ┌──────────────────────────────────────────────────────────────────┐
               * │ ⚠️⚠️ A THIRD OPTION, NOT A FIELD BESIDE IT — item 25, 19 Sep.               │
               * └──────────────────────────────────────────────────────────────────┘
               *
               * This control asks **where a type's entitlement comes from**, and *days earned by
               * working* is a third answer to it. The first attempt put a yes/no field next to this
               * one — which made the destination a property of *Szabadság kvóta* rather than an
               * alternative to it, and left a type both granted by an administrator and fed by
               * overtime, which is two sources for one number.
               */}
              <Field label={t('settings.leave.column.quota')}>
                <Select
                  aria-label={t('settings.leave.column.quota')}
                  value={draft.quotaSource}
                  onChange={(event) => {
                    const next = event.target.value;
                    const quotaSource =
                      next === 'unlimited' || next === 'overtime' ? next : 'quota';
                    /**
                     * ⚠️⚠️ **AND THE CARRY RULE GOES WITH IT.** Leaving `max5` on a draft whose
                     * fields have just disappeared would save a rule nobody can see or edit, and
                     * `leaveFrameWindow` reads it — so an unlimited type would quietly keep
                     * offering last year's frame in the request picker. The server normalises the
                     * same way; this is so the form and the row never disagree.
                     */
                    setDraft({
                      ...draft,
                      quotaSource,
                      ...(quotaSource === 'quota'
                        ? {}
                        : { carryOver: 'none' as const, carryToMonth: null }),
                    });
                  }}
                  options={[
                    { value: 'quota', label: t('settings.leave.qsrc.quota') },
                    { value: 'unlimited', label: t('settings.leave.qsrc.unlimited') },
                    { value: 'overtime', label: t('settings.leave.qsrc.overtime') },
                  ]}
                />
              </Field>
            </div>

            {/* ⚠️ A switch with its consequence written beside it, which is what the old yes/no
                dropdown could not say: back-dating OFF is why a sick day cannot be filed. */}
            <div className="flex items-center gap-2.5 rounded-lg border p-2.5">
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-medium">
                  {t('settings.leave.backdate')}
                </span>
              </span>
              <Switch
                checked={draft.backdate}
                label={t('settings.leave.backdate')}
                onCheckedChange={(checked) => {
                  setDraft({ ...draft, backdate: checked });
                }}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {/**
               * ┌────────────────────────────────────────────────────────────┐
               * │ ⚠️⚠️ ONLY AN ANNUAL ENTITLEMENT CARRIES — the ruling of 20 Sep.       │
               * └────────────────────────────────────────────────────────────┘
               *
               * *Korlátlan (nincs keret)* has no quota to carry — the label says so — and a
               * *Túlóra kvóta* balance is EARNED by working rather than granted for a year, so it
               * has no year-end to survive. The pair was drawn for all three sources, which put a
               * carry deadline on two types that have no frame to carry into.
               *
               * ⚠️⚠️ **HIDDEN HERE, THOUGH `Meddig használható fel` IS ONLY DISABLED BELOW, AND
               * THE TWO ARE DIFFERENT STATEMENTS.** A greyed deadline says *not while nothing
               * carries* — the question still applies, the answer is just unavailable. A carry
               * rule on an unlimited type is not unavailable; the question does not exist, and a
               * greyed-out control would invite the reader to wonder how to enable it.
               */}
              {draft.quotaSource !== 'quota' ? null : (
                <>
                  <Field label={t('settings.leave.column.carry')}>
                    <Select
                      aria-label={t('settings.leave.column.carry')}
                      value={draft.carryOver}
                      onChange={(event) => {
                        const next = event.target.value;
                        const carryOver: LeaveCarryOver =
                          next === 'full' || next === 'max5' ? next : 'none';
                        /* ⚠️ The month goes with the rule: a deadline for a carry-over that does not
                       happen is a value nobody can act on, and the column check refuses it. */
                        setDraft({
                          ...draft,
                          carryOver,
                          carryToMonth: carryOver === 'none' ? null : (draft.carryToMonth ?? 2),
                        });
                      }}
                      options={[
                        { value: 'none', label: t('settings.leave.carry.none') },
                        { value: 'full', label: t('settings.leave.carry.full') },
                        { value: 'max5', label: t('settings.leave.carry.max5') },
                      ]}
                    />
                  </Field>

                  <Field label={t('settings.leave.carryMonth')}>
                    <Select
                      aria-label={t('settings.leave.carryMonth')}
                      /* ⚠️ Disabled rather than hidden — the mockup's own choice, and the better one: a
                     field that vanishes makes the reader wonder whether the deadline still applies
                     somewhere. Greyed out, it says *not while nothing carries*. */
                      disabled={draft.carryOver === 'none'}
                      value={String(draft.carryToMonth ?? 2)}
                      onChange={(event) => {
                        setDraft({ ...draft, carryToMonth: Number(event.target.value) });
                      }}
                      options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => ({
                        value: String(month),
                        label: t('settings.leave.carryTo', { month }),
                      }))}
                    />
                  </Field>
                </>
              )}

              <Field label={t('settings.leave.note')}>
                <Select
                  aria-label={t('settings.leave.note')}
                  value={draft.noteRule}
                  onChange={(event) => {
                    const next = event.target.value;
                    setDraft({
                      ...draft,
                      noteRule: next === 'required' || next === 'hidden' ? next : 'optional',
                    });
                  }}
                  options={[
                    { value: 'optional', label: t('settings.leave.rule.optional') },
                    { value: 'required', label: t('settings.leave.rule.required') },
                    { value: 'hidden', label: t('settings.leave.rule.hidden') },
                  ]}
                />
              </Field>

              <Field label={t('settings.leave.attachment')}>
                <Select
                  aria-label={t('settings.leave.attachment')}
                  value={draft.attachmentRule}
                  onChange={(event) => {
                    setDraft({
                      ...draft,
                      attachmentRule: event.target.value === 'required' ? 'required' : 'optional',
                    });
                  }}
                  options={[
                    { value: 'optional', label: t('settings.leave.rule.optional') },
                    { value: 'required', label: t('settings.leave.rule.required') },
                  ]}
                />
              </Field>
            </div>
          </>
        )}
      </EditorDialog>
    </div>
  );
}

/**
 * The twenty symbols a leave type may wear.
 *
 * ⚠️ **A grid of real buttons, not a dropdown of icon names.** The thing being chosen is a picture;
 * a list saying *"shield-check"* asks the reader to imagine one. Each button carries the icon's name
 * as its accessible label, because a button whose only content is an `aria-hidden` glyph has none.
 */
function SymbolPicker({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (icon: string) => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium">{t('settings.leave.symbol')}</span>
      <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-10">
        {LEAVE_SYMBOLS.map((symbol: IconName) => (
          <button
            key={symbol}
            type="button"
            aria-label={symbol}
            aria-pressed={value === symbol}
            className={`grid h-9 place-items-center rounded-md border transition-colors ${
              value === symbol ? 'border-primary ring-primary/30 ring-2' : 'hover:bg-accent'
            }`}
            onClick={() => {
              onChange(symbol);
            }}
          >
            <Icon name={symbol} size={15} />
          </button>
        ))}
      </div>
    </div>
  );
}
